import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import FeedTab from "../components/community/FeedTab";
import ActivitiesTab from "../components/community/ActivitiesTab";
import MapTab from "../components/community/MapTab";
import MembersTab from "../components/community/MembersTab";
import RidesTab from "../components/community/RidesTab";
import SaviPassport from "../components/SaviPassport";
import GuestMatches from "../components/GuestMatches";
import PassportLeaderboard from "../components/PassportLeaderboard";
import PortalBottomNav from "../components/PortalBottomNav";
import PortalHeader from "../components/PortalHeader";
import { useT } from "../lib/i18n";
import {
  ArrowLeft,
  Users,
  MapPin,
  Calendar,
  Coffee,
  Sparkles,
  Loader2,
  Home,
  Bell,
  User,
  Car,
} from "lucide-react";

const RETREAT_BLOCKED_TABS = ["feed", "rides", "map", "matches", "game", "ai"];

const CommunityPage = () => {
  const { user, token } = useAuth();
  const isRetreat = user?.guest_type === "retreat";
  const { t, isDe } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const tab = searchParams.get("tab") || "feed";
    return isRetreat && RETREAT_BLOCKED_TABS.includes(tab) ? "activities" : tab;
  });
  const [loading, setLoading] = useState(true);

  // Keep the active tab in sync when the bottom nav changes ?tab= without remounting.
  // Retreat guests are restricted to Calendar + People — anything else bounces to the programme.
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (isRetreat && (!tab || RETREAT_BLOCKED_TABS.includes(tab))) {
      setSearchParams({ tab: "activities" }, { replace: true });
      return;
    }
    if (tab) setActiveTab(tab);
  }, [searchParams, isRetreat, setSearchParams]);

  // Data states
  const [introductions, setIntroductions] = useState([]);
  const [upcomingArrivals, setUpcomingArrivals] = useState([]);
  const [energyStats, setEnergyStats] = useState(null);
  const [todayEnergy, setTodayEnergy] = useState(null);
  const [communityStats, setCommunityStats] = useState(null);
  const [alumni, setAlumni] = useState([]);
  const [espressoTalks, setEspressoTalks] = useState([]);
  const [weeklySchedule, setWeeklySchedule] = useState([]);
  const [communityProfiles, setCommunityProfiles] = useState([]);
  const [communityPets, setCommunityPets] = useState({ pets: [], counts: { dogs: 0, cats: 0, total: 0 } });
  const [unreadCount, setUnreadCount] = useState(0);
  const [rides, setRides] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchCommunityData();
  }, []);

  const fetchCommunityData = async () => {
    try {
      const [
        introRes,
        arrivalsRes,
        energyRes,
        myEnergyRes,
        statsRes,
        alumniRes,
        talksRes,
        scheduleRes,
        profilesRes,
        petsRes,
        notificationsRes,
        announcementsRes,
        ridesRes
      ] = await Promise.all([
        axios.get(`${API}/community/introductions`, { headers }),
        axios.get(`${API}/community/upcoming-arrivals`, { headers }),
        axios.get(`${API}/community/energy-stats`, { headers }),
        axios.get(`${API}/community/my-energy`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/community/stats`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/community/alumni`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/community/espresso-talks`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/community/weekly-schedule`, { headers }).catch(() => ({ data: { calendar: [], templates: [] } })),
        axios.get(`${API}/community/profiles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/community/pets`, { headers }).catch(() => ({ data: { pets: [], counts: { dogs: 0, cats: 0, total: 0 } } })),
        axios.get(`${API}/notifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/announcements`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/rides`, { headers }).catch(() => ({ data: [] })),
      ]);

      setIntroductions(introRes.data);
      setUpcomingArrivals(arrivalsRes.data);
      setEnergyStats(energyRes.data);
      setTodayEnergy(myEnergyRes.data?.energy);
      setCommunityStats(statsRes.data);
      setAlumni(alumniRes.data || []);
      setEspressoTalks(talksRes.data || []);
      setWeeklySchedule(scheduleRes.data?.calendar || []);
      setCommunityProfiles(profilesRes.data || []);
      setCommunityPets(petsRes.data || { pets: [], counts: { dogs: 0, cats: 0, total: 0 } });
      setRides(ridesRes.data || []);

      // Calculate unread count
      const unreadNotifications = (notificationsRes.data || []).filter(n => !n.read).length;
      const unreadAnnouncements = (announcementsRes.data || []).filter(a => !a.read).length;
      setUnreadCount(unreadNotifications + unreadAnnouncements);
    } catch (error) {
      console.error("Error fetching community data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#C85A38]" />
      </div>
    );
  }

  const isEventsTab = activeTab === "activities";

  return (
    <>
      <div className={`min-h-screen bg-[#FBF7F1] savi-bg-pattern animate-fade-in pb-24 ${isEventsTab ? "events-fullscreen-mode" : ""}`}>
        {/* Sticky top bar — back + section title (sections open from the bottom nav & home tiles) */}
        <PortalHeader
          className="events-top-strip"
          testid="community-header"
          titleTestid="community-section-title"
          title={{ feed: t("Feed"), rides: t("Rides"), activities: isDe ? "Kalender" : "Calendar", members: t("People"), matches: t("People"), game: t("People"), ai: t("People"), map: t("Map") }[activeTab] || t("Community")}
        />

        {/* People segments — Residents | Matches | House Points */}
        {["members", "matches", "game", "ai"].includes(activeTab) && (
          <div className="max-w-2xl mx-auto px-3 sm:px-4 pt-3">
            <div className={`grid ${isRetreat ? "grid-cols-1" : "grid-cols-3"} bg-white border border-stone-200 rounded-full p-1`} data-testid="people-segments">
              {(isRetreat ? [{ id: "members", label: t("Everyone on the retreat") }] : [
                { id: "members", label: "Residents" },
                { id: "matches", label: "Matches" },
                { id: "game", label: "House Points" },
              ]).map((s) => {
                const isActive = activeTab === s.id || (s.id === "game" && activeTab === "ai");
                return (
                  <button
                    key={s.id}
                    onClick={() => setSearchParams({ tab: s.id })}
                    className={`rounded-full py-1.5 text-xs font-semibold transition-colors active:scale-[0.97] ${
                      isActive ? "bg-[#C85A38] text-white" : "text-[#1A1A1A]/60 hover:text-[#1A1A1A]"
                    }`}
                    data-testid={`people-segment-${s.id}`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-3 sm:px-4 py-3 sm:py-5 events-content-wrap">
            {activeTab === "feed" && !isRetreat && (
              <FeedTab
                user={user}
                headers={headers}
                energyStats={energyStats}
                todayEnergy={todayEnergy}
                espressoTalks={espressoTalks}
                upcomingArrivals={upcomingArrivals}
                onRefresh={fetchCommunityData}
              />
            )}
            {activeTab === "rides" && !isRetreat && (
              <RidesTab user={user} rides={rides} headers={headers} onRefresh={fetchCommunityData} />
            )}
            {activeTab === "activities" && (
              <ActivitiesTab weeklySchedule={weeklySchedule} espressoTalks={espressoTalks} headers={headers} onRefresh={fetchCommunityData} communityProfiles={communityProfiles} />
            )}
            {(activeTab === "members" || (isRetreat && ["matches", "game", "ai"].includes(activeTab))) && (
              <MembersTab communityProfiles={communityProfiles} communityPets={communityPets} introductions={introductions} isRetreat={isRetreat} />
            )}
            {activeTab === "matches" && !isRetreat && <GuestMatches token={token} />}
            {activeTab === "map" && !isRetreat && <MapTab communityStats={communityStats} alumni={alumni} />}
            {(activeTab === "ai" || activeTab === "game") && !isRetreat && (
              <div className="space-y-4">
                <SaviPassport token={token} />
                <PassportLeaderboard currentUserId={user?.id} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation - Outside animated container for fixed positioning */}
      <PortalBottomNav active={activeTab === "activities" ? "calendar" : ["members", "matches", "game", "ai"].includes(activeTab) ? "people" : null} />
    </>
  );
};

export default CommunityPage;
